/**
 * Synthesised instruments, for when recordings are not available.
 *
 * A raw sawtooth does not sound like a guitar, and no amount of envelope shaping
 * makes it one. These use models of how the instruments actually make sound, which
 * is a different thing from imitating the result:
 *
 *   Guitar — Karplus–Strong. A burst of noise is fed into a delay line one period
 *   long with a lowpass filter in the feedback path. That is very close to what a
 *   plucked string physically does: the initial excitation is broadband, the string
 *   selects its harmonic series, and the high partials die first. It sounds like a
 *   pluck because it is built like one.
 *
 *   Piano — FM. A struck string is inharmonic and its brightness collapses far
 *   faster than its volume, which is exactly what a fast-decaying modulator over a
 *   slow-decaying carrier produces. Two layers, one for the strike and one for the
 *   body.
 *
 * These are honest approximations, not fakes of a specific recording. Running
 * `npm run fetch-samples` replaces them with the real thing.
 */

import * as Tone from 'tone';

export interface Voice {
  triggerAttackRelease(
    notes: number | number[],
    duration: number,
    time?: number,
    velocity?: number,
  ): void;
  releaseAll(): void;
  dispose(): void;
}

/**
 * Karplus–Strong is inherently one-note-per-instance, so polyphony comes from a
 * pool of plucks handed out in rotation. Eight covers any chord this app builds
 * with room for the previous one to still be ringing.
 */
export class PluckPool implements Voice {
  private voices: Tone.PluckSynth[];
  private next = 0;

  constructor(destination: Tone.InputNode, size = 8) {
    this.voices = Array.from({ length: size }, () =>
      new Tone.PluckSynth({
        attackNoise: 0.9, // brightness of the initial pluck
        dampening: 3600, // where the string's highs roll off
        resonance: 0.95, // how long it rings
        release: 1.2,
      }).connect(destination),
    );
  }

  /** Index of the voice a note would be given. Exposed so rotation is testable. */
  peekNext(): number {
    return this.next % this.voices.length;
  }

  triggerAttackRelease(notes: number | number[], _duration: number, time?: number, velocity = 0.8) {
    const list = Array.isArray(notes) ? notes : [notes];
    for (const frequency of list) {
      const voice = this.voices[this.next % this.voices.length];
      this.next += 1;
      // PluckSynth takes no velocity argument — the excitation is a fixed noise
      // burst — so dynamics come from the voice's own gain instead.
      voice.volume.value = -6 + (velocity - 0.8) * 14;
      // A plucked string is not "held": its length comes from the decay of the
      // delay line, so the requested duration does not become a release time.
      voice.triggerAttack(frequency, time);
    }
  }

  releaseAll() {
    for (const voice of this.voices) voice.triggerRelease(Tone.now());
  }

  dispose() {
    for (const voice of this.voices) voice.dispose();
    this.voices = [];
  }
}

/** Two layers: an FM strike with a fast-collapsing modulator, and a soft body. */
export class PianoVoice implements Voice {
  private strike: Tone.PolySynth<Tone.FMSynth>;
  private body: Tone.PolySynth<Tone.Synth>;

  constructor(destination: Tone.InputNode) {
    this.strike = new Tone.PolySynth(Tone.FMSynth).connect(destination);
    this.strike.set({
      harmonicity: 2.5,
      modulationIndex: 11,
      oscillator: { type: 'sine' },
      envelope: { attack: 0.002, decay: 1.9, sustain: 0.04, release: 1.1 },
      // Brightness dies long before loudness does — this is what makes a struck
      // string read as struck rather than blown.
      modulation: { type: 'sine' },
      modulationEnvelope: { attack: 0.002, decay: 0.22, sustain: 0, release: 0.2 },
    });
    this.strike.volume.value = -12;

    this.body = new Tone.PolySynth(Tone.Synth).connect(destination);
    this.body.set({
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.004, decay: 2.4, sustain: 0.06, release: 1.6 },
    });
    this.body.volume.value = -19;
  }

  triggerAttackRelease(notes: number | number[], duration: number, time?: number, velocity = 0.8) {
    this.strike.triggerAttackRelease(notes, duration, time, velocity);
    this.body.triggerAttackRelease(notes, duration, time, velocity * 0.75);
  }

  releaseAll() {
    this.strike.releaseAll();
    this.body.releaseAll();
  }

  dispose() {
    this.strike.dispose();
    this.body.dispose();
  }
}

export function createFallbackVoice(instrument: 'piano' | 'guitar', destination: Tone.InputNode): Voice {
  return instrument === 'guitar' ? new PluckPool(destination) : new PianoVoice(destination);
}
